import React, { useEffect, useState } from 'react';
import { can } from '../permissions';
import { Calendar, X, Truck, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

export default function Rescheduled() {
  const [list, setList] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignModal, setAssignModal] = useState(null);
  const [selDriver, setSelDriver] = useState('');
  const [selDate, setSelDate] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/invoices/rescheduled-list').then(r => setList(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/drivers').then(r => setDrivers(r.data));
  }, []);

  const openAssign = (inv) => {
    setAssignModal(inv);
    setSelDriver('');
    setSelDate(inv.reschedule_date ? inv.reschedule_date.split('T')[0] : new Date().toISOString().split('T')[0]);
  };

  const confirmAssign = async () => {
    if (!selDriver || !selDate) return toast.error('Select driver and date');
    try {
      await api.post('/invoices/assign-rescheduled', {
        invoice_id: assignModal.id,
        driver_name: selDriver,
        session_date: selDate
      });
      toast.success('Added to route — visit Route Planner to optimize and assign');
      setAssignModal(null);
      load();
    } catch { toast.error('Error assigning'); }
  };

  const dismiss = async (inv) => {
    if (!window.confirm(`Dismiss ${inv.customer_name}? This marks it as permanently cancelled.`)) return;
    try {
      await api.post('/invoices/dismiss-rescheduled/' + inv.id);
      toast.success('Dismissed');
      load();
    } catch { toast.error('Error'); }
  };

  const isOverdue = (date) => date && new Date(date) < new Date(new Date().toDateString());

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>Rescheduled Deliveries</h1>
          <p style={{ color: '#64748b', fontSize: 13, margin: '4px 0 0' }}>
            Customers who requested redelivery on another date — assign to a driver's route or dismiss
          </p>
        </div>
        <div style={{ background: '#ede9fe', color: '#7c3aed', padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
          {list.length} pending
        </div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>}

      {!loading && !list.length && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 50, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <Calendar size={40} color="#cbd5e1" style={{ marginBottom: 12 }} />
          <div style={{ color: '#94a3b8', fontSize: 14 }}>No rescheduled deliveries pending</div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {list.map(inv => {
          const overdue = isOverdue(inv.reschedule_date);
          return (
            <div key={inv.id} style={{
              background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: overdue ? '1px solid #fca5a5' : '1px solid transparent'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{inv.customer_name}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{inv.customer_code} · {inv.salesman_name}</div>
                </div>
                <div style={{
                  padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  background: overdue ? '#fee2e2' : '#ede9fe', color: overdue ? '#dc2626' : '#7c3aed',
                  display: 'flex', alignItems: 'center', gap: 4
                }}>
                  {overdue && <AlertCircle size={11} />}
                  {inv.reschedule_date ? new Date(inv.reschedule_date).toLocaleDateString() : 'No date'}
                </div>
              </div>

              <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600, marginBottom: 4 }}>
                AED {Number(inv.invoice_value || 0).toLocaleString()}
              </div>

              {inv.original_reason && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, fontStyle: 'italic' }}>
                  Reason: {inv.original_reason}
                </div>
              )}

              {can('rescheduled','assign') && <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button onClick={() => openAssign(inv)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 12px', background: '#0284c7', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <Truck size={13} /> Assign to Route
                </button>
                <button onClick={() => dismiss(inv)}
                  style={{ padding: '9px 12px', background: '#fef2f2', border: 'none', borderRadius: 8, color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <X size={13} />
                </button>
              </div>}
            </div>
          );
        })}
      </div>

      {/* Assign modal */}
      {assignModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 4 }}>🚚 Assign to Driver Route</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>{assignModal.customer_name}</div>

            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Delivery date</label>
            <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }} />

            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Assign to driver</label>
            <select value={selDriver} onChange={e => setSelDriver(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 20 }}>
              <option value="">Select driver</option>
              {drivers.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>

            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16, background: '#f8fafc', padding: '10px 12px', borderRadius: 8 }}>
              💡 This adds the invoice to that driver's session for the selected date. Visit Route Planner afterward to optimize and assign the full route.
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setAssignModal(null)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={confirmAssign} style={{ flex: 1, padding: 10, background: '#0284c7', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Add to Route</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
